import { useRef } from 'react';
import { useNavigate } from 'react-router';
import { useAppToast } from '../../../hooks/ui/useAppToast';
import { useEntityNavigation } from '../../../hooks/entity/useEntityNavigation';

import { Glyph } from '../../ui/Glyph';
import { ENTITY_ICONS, getEntityDisplayName } from '../../../theme/entityIcons';
import type { EntityType } from '../../../theme/entityIcons';
import type { ChatContext } from '../../../contexts/ChatContextProvider';
import { ROUTES } from '../../../constants/routes';
import { ComposerChip } from './ComposerChip';
import { ContextSelectionTag, contextChipName } from './ContextSelectionTag';
import { useLiveContextDetail } from './useContextSelection';

interface ContextChipProps {
  context: ChatContext;
  onRemove: () => void;
  onChangeContext?: () => void;
  /** Stop sending this context's selected cells (a spreadsheet's range). */
  onDismissSelection?: () => void;
}

/**
 * ContextChip - Visual indicator of the current entity context being used for chat
 * Similar to Notion's context selection UI
 * Click to navigate to the context entity (projects) or open in side panel (notes/attachments)
 */
function ContextChip({
  context,
  onRemove,
  onChangeContext: _onChangeContext,
  onDismissSelection,
}: ContextChipProps) {
  const navigate = useNavigate();
  const toast = useAppToast();
  const { openEntity } = useEntityNavigation();

  const glyph = ENTITY_ICONS[context.type as EntityType]?.glyph ?? ENTITY_ICONS.note.glyph;
  const label = context.name || getEntityDisplayName(context.type);
  // Live, and exactly what the next send carries (see useContextSelection).
  const { sheet, selection } = useLiveContextDetail(context);
  // A context with a live view (a spreadsheet) names itself in full here: the
  // chip truncates the title and never shows the sheet.
  const tooltip = context.detail
    ? `${[label, sheet && `sheet ${sheet}`].filter(Boolean).join(' · ')}. Click to open.`
    : `Click to open ${getEntityDisplayName(context.type)}`;

  const handleChipClick = () => {
    try {
      if (context.type === 'project') {
        navigate(ROUTES.collection(context.id));
      } else {
        // Artifacts (note, protocol, attachment, …) — shared single nav path.
        openEntity(context.type, context.id, { surface: 'context_chip' });
      }
    } catch (error) {
      toast({
        title: `Error opening ${context.type}`,
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // The range's remove button unmounts with the range, so focus moves to the
  // chip itself (its group: always enabled, no tooltip, no on-screen
  // keyboard), in every case. Not the open button, whose tooltip opens on
  // focus, and not the message box, which is disabled mid-stream and opens a
  // touch keyboard over the grid.
  const groupRef = useRef<HTMLDivElement>(null);
  const handleDismissSelection = onDismissSelection
    ? () => {
        onDismissSelection();
        groupRef.current?.focus();
      }
    : undefined;

  return (
    // Chip geometry lives in ComposerChip so this row and the attachment row
    // directly below it stay pixel-identical. The tooltip sits on the open
    // button only (`clickTooltip`): on the whole chip it also fired over
    // both remove controls, which do not open anything.
    <ComposerChip
      well={<Glyph {...glyph} size="14px" gradient="warm" />}
      label={label}
      onClick={handleChipClick}
      clickLabel={`Open ${label}`}
      clickTooltip={tooltip}
      // Only a chip that can carry a dismissible range (a live view, i.e. a
      // spreadsheet) needs a focus target; every other context chip keeps no
      // tabindex. Keyed on `detail`, not on a range being shown now: the range
      // is gone by the time focus moves.
      groupRef={context.detail && onDismissSelection ? groupRef : undefined}
      onRemove={onRemove}
      removeLabel={`Remove ${label} from context`}
      groupLabel={contextChipName(label, sheet, selection)}
      // The range pill is never truncated, so the title gives way to it.
      labelMaxW={selection ? '120px' : undefined}
    >
      <ContextSelectionTag selection={selection} onDismissSelection={handleDismissSelection} />
    </ComposerChip>
  );
}

export default ContextChip;
